import { Core } from "@faire/design-tokens";
import { THREE_LETTER_PRIMARY_COUNTRY_CODES } from "@faire/web--source/common/consts/THREE_LETTER_PRIMARY_COUNTRY_CODES";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { Button, ButtonProps } from "@faire/web--source/slate/Button";
import styled from "styled-components";

import { RetailerStore } from "@faire/retailer/stores/domain/RetailerStore";

interface IInternationalTestimonialData {
  testimonial: string;
  brandName: string;
  brandOwnerName: string;
  profileImageUrl: string;
  nameSubtitle: string;
  logoUrl?: string;
}

export interface ICategoryGroup {
  eventKey: string;
  backgroundColor: string;
  header: string;
  logoImageUrl: string;
  mobileLogoImageUrl: string;
  opacity?: number;
  featureImageUrl: string;
  profileImageUrl: string;
  brandLogoUrl?: string;
  testimonial: string;
  brandOwnerName: string;
  brandOwnerNameSubtitle: string;
  internationalTestimonialData?: IInternationalTestimonialData;
}

export const getCategoriesGroup = (
  isSSR: boolean,
  strictLocalize: StrictLocalizeFunction
): ICategoryGroup[] => [
  {
    eventKey: "apparel",
    backgroundColor: "#f8f8f4",
    header: strictLocalize({
      defaultMessage: "Apparel",
      description: { text: "Category name" },
    }),
    logoImageUrl:
      "https://cdn.faire.com/fastly/9cde97de68963f0ab16be20646b75f67eba07857b3b27475164554fae4aef68b.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/cd36f86d59f3d9bc183cf55aa2d124f37e310a0c960c86a6bf1ebfe15eeb3cd0.png",
    testimonial: strictLocalize({
      defaultMessage:
        '"The Faire team invested in building a relationship with us which is hard to come by in this industry."',
    }),
    brandOwnerName: strictLocalize({
      defaultMessage: "Robert & Carley",
      description: {
        text: "Names of the brand owners - there are two owners of this brand",
      },
    }),
    brandOwnerNameSubtitle: strictLocalize({
      defaultMessage: "Founders, Poppy + Sage",
      description: { text: "These are the founders of the brand Poppy + Sage" },
    }),
    featureImageUrl:
      "https://cdn.faire.com/fastly/b1d00c61401d497615e6396ee6c6fc96dc50a68c6eccc1c7d6f949e64887d409.png",
    profileImageUrl:
      "https://cdn.faire.com/fastly/8e1a907d15514e987092c1bdf1167362474393dc99fd6406992b1b2466fa9aac.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/f54f39f67dce3436e710c74a4f944822066b57c5f77ebb9f0f7a25f082201bea.png",
  },
  {
    eventKey: "paper-and-novelty",
    backgroundColor: "#77372F",
    header: strictLocalize({
      defaultMessage: "Paper & Novelty",
      description: { text: "Category name" },
    }),
    logoImageUrl:
      "https://cdn.faire.com/fastly/c84b77b612a700e4d6992c8be5109f86e8216385176abbb908f5aba583028abd.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/7ad6c2ccf88127bd105b7bc9e9b5cda2a2d2e7214c2dd782903abc5a9875edb2.png",
    opacity: 20,
    testimonial: strictLocalize({
      defaultMessage:
        '"We have watched our sales on the marketplace explode exponentially. We can\'t imagine doing business without Faire!"',
    }),
    brandOwnerName: strictLocalize({
      defaultMessage: "Hal & Carrie Mertz",
      description: {
        text: "Names of the brand owners - there are two owners of this brand",
      },
    }),
    brandOwnerNameSubtitle: strictLocalize({
      defaultMessage: "Founders, Red Cap Cards",
      description: {
        text: "These are the founders of the brand Red Cap Cards",
      },
    }),
    profileImageUrl:
      "https://cdn.faire.com/fastly/9be8a22b9b9f6d7ecb6863f32f021a8ee1c5843b49ebc87b9bea23bb34bed975.png",
    featureImageUrl:
      "https://cdn.faire.com/fastly/6b9e6a5e8d8f7fb16d870ed8e1d98b2668a0419c918a65e818839c0393d5aa43.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/392e557051cf54c15378b84da17dc9bd8e4555eff11c5594ebad23a4c1bfcc12.png",
    internationalTestimonialData: {
      testimonial: strictLocalize({
        defaultMessage:
          '"Our strategies have changed because we’ve learned so much from Faire."',
      }),
      brandOwnerName: "Sylvain Favardin",
      brandName: "Scrollino",
      nameSubtitle: "Scrollino",
      profileImageUrl:
        "https://cdn.faire.com/fastly/ccfcdf6af3e2b242d6a855ddf0c31da2db9e5b2cdca7abd8e84b2887c0b77bb4.jpeg",
      logoUrl: optimizeImageUrl(
        {
          url: "https://cdn.faire.com/fastly/00ba781aa326030f48303d1f03672b1a104fb635cb562508b5549e9d86a23a26.png",
        },
        isSSR,
        { height: 20 }
      ),
    },
  },
  {
    eventKey: "kids-and-baby",
    backgroundColor: "#527A73",
    header: strictLocalize({
      defaultMessage: "Kids & Baby",
      description: { text: "Category name" },
    }),
    logoImageUrl:
      "https://cdn.faire.com/fastly/ccb1c1df518d437e5e1fce4ee5044dd51b069e18fb0a62fe13678ba13af4e021.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/46c9bf90d562642a0e92743be9805e4719febf3e84c3268bd346840f367367ed.png",
    opacity: 20,
    testimonial: strictLocalize({
      defaultMessage:
        '"Faire has introduced my brand to stores I would have never found or thought about contacting."',
    }),
    brandOwnerName: "Wendy Carter",
    brandOwnerNameSubtitle: strictLocalize({
      defaultMessage: "Founder, Yikes Twins",
      description: { text: "These are the founders of the brand Yikes Twins" },
    }),
    featureImageUrl:
      "https://cdn.faire.com/fastly/1ae8a3a08c127e2f57a3970f3a117061d1da4d8f1121adbe2bfbfcf0eeca56e8.png",
    profileImageUrl:
      "https://cdn.faire.com/fastly/2f6ed9ce563a362a23ac19a4490ee325599bc31ab7c4e7a13ba3f6ec51fd263c.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/9ef1bfcc04b5ad3193c12e0a49443fa517fc4dbe865eb25e35bf522cb8764736.png",
  },
  {
    eventKey: "jewelry",
    backgroundColor: "#B96C3D",
    header: strictLocalize({
      defaultMessage: "Jewelry",
      description: { text: "Category name" },
    }),
    logoImageUrl:
      "https://cdn.faire.com/fastly/2907c0c5ff1ce6bbf74771f79ed5b33c05402247f6c1f38303184c784b9b4b25.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/ccdba4f5aa9d3cd054c3cee29f47949c9e2b415d48317f3604c6810ae9263268.png",
    opacity: 20,
    testimonial: strictLocalize({
      defaultMessage:
        '"Faire has introduced me to so many new customers. Customers I would never of had the opportunity to sell to."',
    }),
    brandOwnerName: "Stephanie Kennedy",
    brandOwnerNameSubtitle: strictLocalize({
      defaultMessage: "Founder, Fornash",
      description: { text: "This is the founder of the brand Fornash" },
    }),
    featureImageUrl:
      "https://cdn.faire.com/fastly/33d7e98e2f7a62f4e4392a8672bf20a5d2100d5f78c54a78d87a34bf4aec78e8.png",
    profileImageUrl:
      "https://cdn.faire.com/fastly/84b077bdd4af3874fc141fef4b3de5bc600dd67e6e7f4bbf8074a47362687368.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/09c0d51735bcea8300a722a24423b743f40bf0fbc890d59a0cf62e9d9426c95c.png",
    internationalTestimonialData: {
      testimonial: strictLocalize({
        defaultMessage:
          '"I would say that Faire is THE tool for connecting to independents. I say to people I barely know that this is where you need to be, how you should be operating. There’s so much potential for great relationships with independents."',
      }),
      brandName: "Powder Design",
      brandOwnerName: "Hannah Graham",
      nameSubtitle: strictLocalize({
        defaultMessage: "Head of Marketing, Powder Design",
        description: { text: "This is the founder of the brand Powder Design" },
      }),
      profileImageUrl:
        "https://cdn.faire.com/fastly/1c6e237762ca76f892f78a764b14812bf47672d59989dee5cfac5111ee500f5f.jpeg",
    },
  },
  {
    eventKey: "home-decor",
    backgroundColor: "#AC8447",
    header: strictLocalize({
      defaultMessage: "Home Decor",
      description: { text: "Category name" },
    }),
    logoImageUrl:
      "https://cdn.faire.com/fastly/8ab3d6c444f2b4e4875d6f8a7a68bf3d6645fd7c270917f873ea1462d76b244c.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/c1e8c3ef5a88cddd3d9c9573d9b5922b408f9f1565d8fb591e9c72bdbf646b79.png",
    opacity: 20,
    testimonial: strictLocalize({
      defaultMessage:
        '"Faire has completely changed how I operate my business. Their digital marketplace has made ordering simple, quick, and effective."',
    }),
    brandOwnerName: "Kelly Decker",
    brandOwnerNameSubtitle: strictLocalize({
      defaultMessage: "Founder, JaxKelly",
      description: { text: "These is the founders of the brand JaxKelly" },
    }),
    featureImageUrl:
      "https://cdn.faire.com/fastly/5aad446bc17d9fb614d0676757d7792b78b8857964083bec90227d7e8f85d8be.png",
    profileImageUrl:
      "https://cdn.faire.com/fastly/e4bfcb205b4ec9ab97744fc4912626e1e7fe95561fe51cb09a1e3a9340bb0fac.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/86d0a2fb97a8f7fb9a4f85714fa038d2a4fc1a7f0ac3e238334f21a222bda8fa.png",
    internationalTestimonialData: {
      testimonial: strictLocalize({
        defaultMessage:
          '"Faire Direct has really helped us bring on a lot of potential customers that I don\'t think we would have brought on otherwise."',
      }),
      brandName: "Huski Home",
      brandOwnerName: "Lisa Scott",
      nameSubtitle: "Huski Home",
      profileImageUrl:
        "https://cdn.faire.com/fastly/f370c7cf872bb1f09a6b60367975401b85b6279e37d3002b857191ff33367629.jpeg",
    },
  },
  {
    eventKey: "pets",
    backgroundColor: "#9A8A60",
    header: strictLocalize({
      defaultMessage: "Pets",
      description: { text: "Category name" },
    }),
    logoImageUrl:
      "https://cdn.faire.com/fastly/204e218ce469a5b5cdf50dbef4fe19a9a620add2fe0f3de9d26f7a8cffb8eea6.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/c9158d1e3037e0c9e40c6ceba2fec9c0e12eb07368b61289d3dcf65079017d8c.png",
    opacity: 20,
    testimonial: strictLocalize({
      defaultMessage:
        '"The incentives that Faire offers are a great selling point and helps to convert leads into customers!"',
    }),
    brandOwnerName: "Stephen Calsbeek",
    brandOwnerNameSubtitle: strictLocalize({
      defaultMessage: "Co-owner, Farm Hounds",
      description: { text: "This is the co-owner of the company Farm Hounds" },
    }),
    featureImageUrl:
      "https://cdn.faire.com/fastly/bbc4a4bd571dc141241651f439c072edbb9fa0bb3180db93dd17caf9409be0ef.png",
    profileImageUrl:
      "https://cdn.faire.com/fastly/8ea9651db54268d7fe383b3ba75b9b802c7b28092c1151ba4fff0e6cb683292e.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/43d5d03bd4a17bff77b1b97c51352009d04a079b54f5fc616cd182189570361a.png",
  },
  {
    eventKey: "food-and-drink",
    backgroundColor: "#627988",
    header: strictLocalize({
      defaultMessage: "Food & Drink",
      description: { text: "Category name" },
    }),

    logoImageUrl:
      "https://cdn.faire.com/fastly/e63e0ccf3b2a06a1c2fa28bd3635d856b042132c51fe31929a401b06e3158d3c.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/a82594c59b037e07df4e48869ddf86a30b04490c4c460b75aca756c58de3946f.png",
    opacity: 20,
    testimonial: strictLocalize({
      defaultMessage:
        '"Faire is easy to work with, extremely reliable, and highly scalable."',
    }),
    brandOwnerName: "Sarah Burbach",
    brandOwnerNameSubtitle: "Candy Club",
    featureImageUrl:
      "https://cdn.faire.com/fastly/ec6f9e498204294dd4c57050e619f733edd4c2d7bc35afd0dee9e5191c06617c.png",
    profileImageUrl:
      "https://cdn.faire.com/fastly/2742b582cf47ef666e85dc053d8e0103844fcaf48a6aac2eaa7d1b9b31ad1341.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/9027a8fd691293387661a47f39e93c602cc80e6911bcde8aa02cee2bbd9bb6b0.png",
  },
  {
    eventKey: "beauty",
    backgroundColor: "#B16149",
    header: strictLocalize({
      defaultMessage: "Beauty",
      description: { text: "Category name" },
    }),
    logoImageUrl:
      "https://cdn.faire.com/fastly/3fcf10cc090b9962347d0da754899fbb145ad54b42481ec32330227e7ed4eab9.png",
    mobileLogoImageUrl:
      "https://cdn.faire.com/fastly/56b3942e8bde7abcd8ed5725e43ea2fbaa0a1fd00b517facd600bac1d8fc5dd6.png",
    opacity: 20,
    testimonial: strictLocalize({
      defaultMessage:
        '"Faire is a place for discovery, a place to celebrate our stories, all while making it simple and convenient."',
    }),
    brandOwnerName: "Iris Cherng",
    brandOwnerNameSubtitle: strictLocalize({
      defaultMessage: "Owner, Honey Belle",
      description: {
        text: "This is a reference for a quote from the owner of the brand Honey Belle",
      },
    }),
    featureImageUrl:
      "https://cdn.faire.com/fastly/9e0d21d6aaef384ca8e720bb770221acc3c5354191992f3a035612d05e3fa68e.png",
    profileImageUrl:
      "https://cdn.faire.com/fastly/3fdc7172862ee2e438fc7c74148b9a8c14b84603c15658d6bd6cf6acb5dfd7ec.png",
    brandLogoUrl:
      "https://cdn.faire.com/fastly/d67c0b2413b7b0cfd26be176245735c7c021842b07489966616a1e933bff62c7.png",
  },
];

export interface ITestimonialData {
  brandName: string;
  brandRegion: string;
  brandImageUrl: string;
  brandToken: string;
  ownerName: string;
  ownerCity?: string;
  content: string;
}

export const BASE_TESTIMONIAL_EU = (
  strictLocalize: StrictLocalizeFunction
): ITestimonialData[] => [
  {
    brandName: "Ibiza Tarot",
    brandRegion: strictLocalize({
      defaultMessage: "Switzerland",
      description: { text: "The country name Switzerland" },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/95da2f590782acac042dc4ba03d6f6cfa4ac8ea4490b4d26a49d66dfff3e5417.png",
    brandToken: "b_cbcdwva4s7",
    ownerName: "Carmen",
    ownerCity: strictLocalize({
      defaultMessage: "Bern",
      description: { text: "The city Bern in Switzerland" },
    }),
    content: strictLocalize({
      defaultMessage:
        '"Faire was an absolute game-changer for our brand. Immediately after I signed up, we increased our orders in a way we never could have dreamed of. We got great exposure to shops I wouldn\'t have reached before. Faire made whole sale for us easy, fun and safe!"',
    }),
  },
  {
    brandName: "The Completist",
    brandRegion: strictLocalize({
      defaultMessage: "United Kingdom",
      description: { text: "The country United Kingdom." },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/a4bd7fe2b160e056dbf55e316e2bd5ac3c58f159794996df1065eb2e9fa3fab2.png",
    brandToken: "b_cc58rlj7jb",
    ownerName: "Jana",
    ownerCity: strictLocalize({
      defaultMessage: "London",
      description: { text: "The city London in United Kingdom." },
    }),
    content: strictLocalize({
      defaultMessage:
        "\"Faire has completely transformed our business! We've gone from 5 USA stockists to over 200 in the 2 years we've been selling on here. It's absolutely incredible! We're now stocked in some of the best stores and art institutions in the world, thanks to Faire.\"",
    }),
  },
  {
    brandName: "Minefuel",
    brandRegion: strictLocalize({
      defaultMessage: "Germany",
      description: { text: "The country Germany" },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/eddadf060ab4cb66e04b9ad7a045711f7b66eb7222c1fd73c757899fe280d3c5.jpeg?dpr=2&fit=crop&format=jpg&height=96&width=96",
    brandToken: "b_869oiwzo1b",
    ownerName: "Vadim",
    content: strictLocalize({
      defaultMessage:
        '"With Faire we have found a lot of retailers from all over the world who share our philosophy and are fond of high quality organic products and open-minded enough to try them!"',
    }),
  },
];

export const BASE_TESTIMONIAL_NAM = (
  strictLocalize: StrictLocalizeFunction
): ITestimonialData[] => [
  {
    brandName: "Jax Kelly",
    brandRegion: "CA",
    brandImageUrl:
      "https://cdn.faire.com/fastly/57c46aeebeeadbf2a49aaca2aa781442dfcd8daacaa7d1a1ffb6a8434a0bd2a3.png",
    brandToken: "b_f0fbd5c7",
    ownerName: "Kelly Decker",
    ownerCity: "San Diego",
    content: strictLocalize({
      defaultMessage:
        '"Faire has completely changed how I operate my business. Their digital marketplace has made ordering simple, quick and effective."',
    }),
  },
  {
    brandName: "Red Cap Cards",
    brandRegion: "CA",
    brandImageUrl:
      "https://cdn.faire.com/fastly/e717bb1ad2f2d2d0c5283b5c16c9354759501980d66ad09f85cb3ca8397ba7f8.png",
    brandToken: "b_4cu4uycf",
    ownerName: strictLocalize({
      defaultMessage: "Hal & Carrie Mertz",
      description: {
        text: "Names of the brand owners - there are two owners of this brand",
      },
    }),
    ownerCity: "Los Angeles",
    content: strictLocalize({
      defaultMessage:
        '"We have watched our sales on the marketplace explode exponentially. We can\'t imagine doing business without Faire!"',
    }),
  },
  {
    brandName: "Poppy + Sage",
    brandRegion: "SC",
    brandImageUrl:
      "https://cdn.faire.com/fastly/8e1a907d15514e987092c1bdf1167362474393dc99fd6406992b1b2466fa9aac.png",
    brandToken: "b_3wsx0a7o34",
    ownerName: strictLocalize({
      defaultMessage: "Robert & Carley",
      description: {
        text: "Names of the brand owners - there are two owners of this brand",
      },
    }),
    ownerCity: "Charleston",
    content: strictLocalize({
      defaultMessage:
        '"The Faire team invested in building a relationship with us which is hard to come by in this industry."',
    }),
  },
];

const TESTIMONIAL_GBR = (
  strictLocalize: StrictLocalizeFunction
): ITestimonialData[] => [
  {
    brandName: "Hip Pop",
    brandRegion: strictLocalize({
      defaultMessage: "United Kingdom",
      description: { text: "The country United Kingdom" },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/7424cc1e3ff52f7acfdbd34432eb2937a5e72d5d1e13c5b9f513044a27a69ffc.png",
    brandToken: "b_czq5hb98ar",
    ownerName: "Kenny",
    ownerCity: strictLocalize({
      defaultMessage: "Manchester",
      description: { text: "Mancheser city in UK" },
    }),
    content: strictLocalize({
      defaultMessage:
        '"Faire has helped us entice new direct customers with their incentives and excellent terms and we have also gained a lot of new customers all over the world through their brilliant marketplace. This has given the confidence that we can become a global brand."',
    }),
  },
  {
    brandName: "The Completist",
    brandRegion: strictLocalize({
      defaultMessage: "United Kingdom",
      description: { text: "The country United Kingdom." },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/a4bd7fe2b160e056dbf55e316e2bd5ac3c58f159794996df1065eb2e9fa3fab2.png",
    brandToken: "b_cc58rlj7jb",
    ownerName: "Jana",
    ownerCity: strictLocalize({
      defaultMessage: "London",
      description: { text: "The city London in United Kingdom." },
    }),
    content: strictLocalize({
      defaultMessage:
        "\"Faire has completely transformed our business! We've gone from 5 USA stockists to over 200 in the 2 years we've been selling on here. It's absolutely incredible! We're now stocked in some of the best stores and art institutions in the world, thanks to Faire.\"",
    }),
  },
  {
    brandName: "Black+Blum",
    brandRegion: strictLocalize({
      defaultMessage: "United Kingdom",
      description: { text: "The country United Kingdom." },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/e8394cfe5107a41f2361c214749df697f337441bee83f7cf6aec8d51eb9bd981.jpeg",
    brandToken: "b_1l55scxr",
    ownerName: "Josh",
    ownerCity: "London",
    content: strictLocalize({
      defaultMessage:
        "\"Faire has been a massive help in unlocking and exposing Black+Blum to thousands of retailers globally. In a world where digital 'window shopping' is now commonplace, the platform has been invaluable to a small business like ours.\"",
    }),
  },
];

const TESTIMONIAL_FRA: ITestimonialData[] = [
  {
    brandName: "Liya S.",
    brandRegion: "France",
    brandImageUrl:
      "https://cdn.faire.com/fastly/0e9ca0140d19e3d2116978301ef9b1f9ce16fc4b0c6f17b4c2c902746e7786ed.png",
    brandToken: "b_ynp67rb56s",
    ownerName: "Liya",
    ownerCity: "Paris",
    content:
      "« La plateforme Faire m'a permis de faire connaitre ma marque à des professionnels, et de pouvoir leurs proposer des avantages comme des promotions, échantillons, etc. Faire est vraiment une plateforme simple d'utilisation qui a développé ma marque à l'échelle internationale. »",
  },
  {
    brandName: "Confiture Parisienne",
    brandRegion: "France",
    brandImageUrl:
      "https://cdn.faire.com/fastly/a5a88f975dacf4da7a59169d4ffc3d5bbea66dbfbb237988805647957cac75ab.jpeg",
    brandToken: "b_bk2kncesz6",
    ownerName: "Gaëlle",
    ownerCity: "Paris",
    content:
      "« La plateforme a été pensée de manière intuitive : de la gestion des stocks, en passant par la prise de commandes, à l'envoi de Newsletters, au suivi via l'onglet \"paiement\" des virements… Tout se fait facilement. »",
  },
  {
    brandName: "OGO Living",
    brandRegion: "France",
    brandImageUrl:
      "https://cdn.faire.com/fastly/03f43bcdf91375d3365e7cfa7efe104afbf914afc4978e8cdb709b429a81bc38.png",
    brandToken: "b_5sy7x5niey",
    ownerName: "Gwenaëlle",
    ownerCity: "Arques",
    content:
      "« Faire nous a permis de faire connaître nos marques sur le marché américain et nous a offert de nouvelles perspective de développement. »",
  },
];

const TESTIMONIAL_DEU: ITestimonialData[] = [
  {
    brandName: "Pacific Spirit",
    brandRegion: "Deutschland",
    brandImageUrl:
      "https://cdn.faire.com/fastly/d1761f296613418a4bee7f54e80a8466a7cb458b9301f2ca3fbfeedca6f1107d.png",
    brandToken: "b_uf97gavxae",
    ownerName: "Birgit",
    ownerCity: "Olsberg",
    content:
      "„Ich bin seit Dezember 2021 auf Faire und konnte in kurzer Zeit neue Kunden gewinnen und alte Kunden wieder aktivieren.“",
  },
  {
    brandName: "Heindesign",
    brandRegion: "Deutschland",
    brandImageUrl:
      "https://cdn.faire.com/fastly/dcd72a21b053c4b5f155fb6517d87ac0738e7bbed2a57e17beb18c568f56ce7e.png",
    brandToken: "b_85jz4yium2",
    ownerName: "Wolfgang",
    ownerCity: "Hagen",
    content:
      "„Dank Faire war es möglich, die Reichweite unserer Marke in den USA zu erhöhen und unsere Produkte vielen neuen Händlern anzubieten.“",
  },
  {
    brandName: "LOIS MATHAR - made in Munich",
    brandRegion: "Deutschland",
    brandImageUrl:
      "https://cdn.faire.com/fastly/ab17bce699ec0da72dfa4b774b5fc66b8e7386e4b81881677dcae4cfe3f4810d.jpeg",
    brandToken: "b_gcqn8kvhdd",
    ownerName: "Ulrike",
    ownerCity: "Munich",
    content:
      '„Faire bietet mir und meinem Label eine internationale Plattform zu ausgesprochen "fairen" Konditionen. Für mich ganz wichtig: ich fühle mich von den Mitarbeitern sehr gut betreut.“',
  },
];

const TESTIMONIAL_ESP = (
  strictLocalize: StrictLocalizeFunction
): ITestimonialData[] => [
  {
    brandName: "BOUSIINNE",
    brandRegion: "España",
    brandImageUrl:
      "https://cdn.faire.com/fastly/c34d2d2d2c299e48a10342afbc43e6013ed3dabdb50b3638831a1e78e3c86e8f.png",
    brandToken: "b_bly0p3exhh",
    ownerName: "Adrianna",
    ownerCity: "Murcia",
    content:
      "«En menos de un mes en Faire, hemos logrado alcanzar el objetivo de llegar al mercado Estadounidense a través de diversas tiendas.»",
  },
  {
    brandName: "Ibiza Tarot",
    brandRegion: strictLocalize({
      defaultMessage: "Switzerland",
      description: { text: "The country Switzerland" },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/95da2f590782acac042dc4ba03d6f6cfa4ac8ea4490b4d26a49d66dfff3e5417.png",
    brandToken: "b_cbcdwva4s7",
    ownerName: "Carmen",
    ownerCity: strictLocalize({
      defaultMessage: "Bern",
      description: { text: "City Bern in Switzerland" },
    }),
    content: strictLocalize({
      defaultMessage:
        '"Faire was an absolute game-changer for our brand. Immediately after I signed up, we increased our orders in a way we never could have dreamed of. We got great exposure to shops I wouldn\'t have reached before. Faire made whole sale for us easy, fun and safe!"',
    }),
  },
  {
    brandName: "The Completist",
    brandRegion: strictLocalize({
      defaultMessage: "United Kingdom",
      description: { text: "The country United Kingdom." },
    }),
    brandImageUrl:
      "https://cdn.faire.com/fastly/a4bd7fe2b160e056dbf55e316e2bd5ac3c58f159794996df1065eb2e9fa3fab2.png",
    brandToken: "b_cc58rlj7jb",
    ownerName: "Jana",
    ownerCity: strictLocalize({
      defaultMessage: "London",
      description: { text: "city london in UK" },
    }),
    content: strictLocalize({
      defaultMessage:
        "\"Faire has completely transformed our business! We've gone from 5 USA stockists to over 200 in the 2 years we've been selling on here. It's absolutely incredible!  We're now stocked in some of the best stores and art institutions in the world, thanks to Faire.\"",
    }),
  },
];

const TESTIMONIAL_ITA: ITestimonialData[] = [
  {
    brandName: "Anthem Brand Co.",
    brandRegion: "Italia",
    brandImageUrl:
      "https://cdn.faire.com/fastly/42bb11168af6ea991b09747f6b6eff154276ef873349f60eec08524727ddfdd8.png",
    brandToken: "b_ryg9y7wdmd",
    ownerName: "Alessandro",
    content:
      '"Crediamo fortemente che Faire con la sua piattaforma possa aiutarci a crescere molto a livello di distribuzione del nostro Brand sia a livello nazionale che internazionale. Il primo ordine che abbiamo ricevuto è arrivato dall\'America e non potremmo essere più felici in quanto il nostro sogno è quello di espandere il nostro business oltre oceano."',
  },
  {
    brandName: "Mojipower",
    brandRegion: "Italia",
    brandImageUrl:
      "https://cdn.faire.com/fastly/c69188dc390a3ab0e3da58e464e6104493335505fc39e9e4f0b4aeb4f4ece91e.png",
    brandToken: "b_40dozf5bc0",
    ownerName: "Laura",
    ownerCity: "Brescia",
    content:
      '"Con Faire sono arrivati nuovi partner e i nostri brand hanno avuto l\'occasione di essere conosciuti da boutique in tutto il mondo!"',
  },
  {
    brandName: "Medea Organics",
    brandRegion: "Italia",
    brandImageUrl:
      "https://cdn.faire.com/fastly/fb143e7eb3883b92c7e0b6a54b1d9922c58c66fba582c801ea345ba2c07db95a.jpeg",
    brandToken: "b_612p2t2cxv",
    ownerName: "Enrico",
    ownerCity: "Catania",
    content:
      '"Grazie a FAIRE il mio brand di complementi d\'arredo artigianali Medea Organics è arrivato negli Stati Uniti, e sono estremamente orgoglioso che Medea Organics faccia parte di FAIRE."',
  },
];

export const TESTIMONIAL_LANGUAGE_GROUP = (
  strictLocalize: StrictLocalizeFunction
): Partial<
  Record<keyof typeof THREE_LETTER_PRIMARY_COUNTRY_CODES, ITestimonialData[]>
> => ({
  USA: BASE_TESTIMONIAL_NAM(strictLocalize),
  CAN: BASE_TESTIMONIAL_NAM(strictLocalize),
  GBR: TESTIMONIAL_GBR(strictLocalize),
  FRA: TESTIMONIAL_FRA,
  DEU: TESTIMONIAL_DEU,
  NLD: BASE_TESTIMONIAL_EU(strictLocalize),
  ITA: TESTIMONIAL_ITA,
  ESP: TESTIMONIAL_ESP(strictLocalize),
  BEL: BASE_TESTIMONIAL_EU(strictLocalize),
  AUT: BASE_TESTIMONIAL_EU(strictLocalize),
  LUX: BASE_TESTIMONIAL_EU(strictLocalize),
  IRL: BASE_TESTIMONIAL_EU(strictLocalize),
  NOR: BASE_TESTIMONIAL_EU(strictLocalize),
  CHE: BASE_TESTIMONIAL_EU(strictLocalize),
  FIN: BASE_TESTIMONIAL_EU(strictLocalize),
  SWE: BASE_TESTIMONIAL_EU(strictLocalize),
  DNK: BASE_TESTIMONIAL_EU(strictLocalize),
  PRT: BASE_TESTIMONIAL_EU(strictLocalize),
});

export const StyledSellOnFaireButton = styled(Button).attrs({
  variant: "secondary",
})<ButtonProps>`
  min-width: 250px;
  width: max-content;

  &:hover {
    background-color: ${Core.surface.inverse};
    color: ${Core.text.hover};
  }
`;

export const IS_PERSONALIZED_LANDING_PAGE_BRAND_TOKEN =
  "IS_PERSONALIZED_LANDING_PAGE_BRAND_TOKEN";

export enum CTAPosition {
  TOP_SECTION = "top-section",
  BOTTOM_SECTION = "bottom-section",
  STICKY_BANNER = "sticky-banner",
}

export const BRANDS_LANDING_PAGE_BELOW_HEADER_IMAGE: string =
  "https://cdn.faire.com/fastly/b8531b5d3211a3f14c13e23475384e5a3eae476a7213cc0a127e3c338daf843b.png";

interface ICaseStudy {
  brandInfo: string;
  sectionTitle: string;
  title: string;
  mobileImage: string;
  desktopImage: string;
  mobilePdf: string;
  desktopPdf: string;
}

const FRANCE_CASE_STUDY = (
  strictLocalize: StrictLocalizeFunction
): ICaseStudy => ({
  brandInfo: strictLocalize({
    defaultMessage: "Scrollino, based in France",
    description: { text: "The brand (Scrollino) is based in France" },
  }),
  sectionTitle: strictLocalize({
    defaultMessage: "What French brands are saying about Faire",
    description: { text: "Title of case study section" },
  }),
  title: strictLocalize({
    defaultMessage:
      "Within months of joining, 60% of Scrollino’s wholesale business was generated by Faire.",
    description: { text: "Title of case study" },
  }),
  mobileImage:
    "https://cdn.faire.com/fastly/ebca9759ef946ab6f53e32c1ba385b6cfe02403d45f1c217a27a3e67b126e83b.png",
  desktopImage:
    "https://cdn.faire.com/fastly/835b37bc127ecf295a9e59227dcb9fcf4344350f4411f69f6058165faa8bcc0d.png",
  mobilePdf: "https://cdn.faire.com/static/mobile-case-study/French.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/Scrollino+Case+study+(French).pdf",
});

const GERMANY_CASE_STUDY = (
  strictLocalize: StrictLocalizeFunction
): ICaseStudy => ({
  brandInfo: strictLocalize({
    defaultMessage: "ånneschd, based in Germany",
    description: { text: "The brand (ånneschd) is based in Germany" },
  }),
  sectionTitle: strictLocalize({
    defaultMessage: "What German brands are saying about Faire",
    description: { text: "Title of case study section" },
  }),
  title: strictLocalize({
    defaultMessage:
      "ånneschd were able to grow sales in new markets for their unique home decor and gifts.",
    description: { text: "Title of case study" },
  }),
  mobileImage:
    "https://cdn.faire.com/fastly/bdbd604a90a2db4df1b5afcfae9c012a36f41b7cbc80737fe677b1612f6b2670.png",
  desktopImage:
    "https://cdn.faire.com/fastly/799d9c546eb73658b0af862dbf0edca7f1ad3ffabd5b48a1ca22333755b3e67d.png",
  mobilePdf: "https://cdn.faire.com/static/mobile-case-study/German.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/German+a%CC%8Anneschd+case+study++-+de-DE.pdf",
});

const UK_CASE_STUDY = (strictLocalize: StrictLocalizeFunction): ICaseStudy => ({
  brandInfo: strictLocalize({
    defaultMessage: "Tabitha Eve, based in the UK",
    description: { text: "The brand (Tabitha Eve) is based in the UK" },
  }),
  sectionTitle: strictLocalize({
    defaultMessage: "What UK brands are saying about Faire",
    description: { text: "Title of case study section" },
  }),
  title: strictLocalize({
    defaultMessage:
      "After Brexit, the Tabitha Eve team found new markets for their ethically made and reusable items with Faire.",
    description: { text: "Title of case study" },
  }),
  mobileImage:
    "https://cdn.faire.com/fastly/f9d68e7f32c9bec599080dfd1fddb55a20ba75bbb5e24825a40f984468426a81.png",
  desktopImage:
    "https://cdn.faire.com/fastly/11d175f51117f9fc0208a763e36cb7a290fb42fd345d19e718616aac57fd5174.png",
  mobilePdf: "https://cdn.faire.com/static/mobile-case-study/UK.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/Tabitha+Eve+case+study+(English).pdf",
});

const DEFAULT_CASE_STUDY_SECTION_TITLE = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage: "What brands are saying about Faire",
    description: { text: "Title of case study section" },
  });

const NETHERLANDS_CASE_STUDY = (
  strictLocalize: StrictLocalizeFunction
): ICaseStudy => ({
  ...FRANCE_CASE_STUDY(strictLocalize),
  sectionTitle: DEFAULT_CASE_STUDY_SECTION_TITLE(strictLocalize),
  mobilePdf: "https://cdn.faire.com/static/mobile-case-study/Dutch.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/Scrollino+Case+study+(Dutch).pdf",
});

const PORTUGAL_CASE_STUDY = (
  strictLocalize: StrictLocalizeFunction
): ICaseStudy => ({
  ...FRANCE_CASE_STUDY(strictLocalize),
  sectionTitle: DEFAULT_CASE_STUDY_SECTION_TITLE(strictLocalize),
  mobilePdf: "https://cdn.faire.com/static/mobile-case-study/Portuguese.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/Scrollino+Case+study+(Portuguese).pdf",
});

const SPAIN_CASE_STUDY = (
  strictLocalize: StrictLocalizeFunction
): ICaseStudy => ({
  ...FRANCE_CASE_STUDY(strictLocalize),
  sectionTitle: DEFAULT_CASE_STUDY_SECTION_TITLE(strictLocalize),
  mobilePdf: "https://cdn.faire.com/static/mobile-case-study/Spanish.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/Scrollino+Case+study+(Spanish).pdf",
});

const ITALY_CASE_STUDY = (
  strictLocalize: StrictLocalizeFunction
): ICaseStudy => ({
  ...FRANCE_CASE_STUDY(strictLocalize),
  sectionTitle: DEFAULT_CASE_STUDY_SECTION_TITLE(strictLocalize),
  mobilePdf: "https://cdn.faire.com/static/mobile-case-study/Italian.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/Scrollino+Case+study+(Italian).pdf",
});

export const DEFAULT_CASE_STUDY = (
  strictLocalize: StrictLocalizeFunction
): ICaseStudy => ({
  ...FRANCE_CASE_STUDY(strictLocalize),
  sectionTitle: DEFAULT_CASE_STUDY_SECTION_TITLE(strictLocalize),
  mobilePdf:
    "https://cdn.faire.com/static/mobile-case-study/Default+English.pdf",
  desktopPdf:
    "https://cdn.faire.com/static/asset/Scrollino+Case+study+(English).pdf",
});

export const CASE_STUDIES = (
  strictLocalize: StrictLocalizeFunction
): Partial<
  Record<keyof typeof THREE_LETTER_PRIMARY_COUNTRY_CODES, ICaseStudy>
> => ({
  GBR: UK_CASE_STUDY(strictLocalize),
  DEU: GERMANY_CASE_STUDY(strictLocalize),
  FRA: FRANCE_CASE_STUDY(strictLocalize),
  NLD: NETHERLANDS_CASE_STUDY(strictLocalize),
  PRT: PORTUGAL_CASE_STUDY(strictLocalize),
  ESP: SPAIN_CASE_STUDY(strictLocalize),
  ITA: ITALY_CASE_STUDY(strictLocalize),
});

export const TRUST_PILOT_MINIMUM_RATING: number = 4.3;

export const getIsShowingCaseStudies = () => RetailerStore.get().isEurope;

export interface IEuropeNewsArticle {
  mediaLogoImageUrl: string;
  mediaLogoHeight: number;
  mediaArticleOverview: string;
  mediaArticleOverviewMobile?: string;
  mediaArticleUrl: string;
}

const NEWS_ARTICAL_INFO_FRA: IEuropeNewsArticle[] = [
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/553d973bd3f0ac8e21a3b95d333dcae2e4fe0aa4e4ece07312ec2345bf44cd62.png",
    mediaLogoHeight: 34,
    mediaArticleOverview:
      "«La marketplace BtoB FAIRE a été fondée en 2017 par ...»",
    mediaArticleOverviewMobile:
      "«La marketplace BtoB FAIRE a été fondée en 2017 par des anciens de Block (ex-Square)...»",
    mediaArticleUrl:
      "https://www.lesechos.fr/start-up/ecosysteme/faire-le-concurrent-americain-dankorstore-qui-veut-conquerir-leurope-1377884",
  },
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/29444f3af53f2360fea697379c28b15c58a81aa184badc6256f305aae971358a.png",
    mediaLogoHeight: 48,
    mediaArticleOverview:
      "«La France est une priorité en Europe,» a récemment...",
    mediaArticleOverviewMobile:
      "«La France est une priorité en Europe,» a récemment déclaré Olivier Buffon...",
    mediaArticleUrl:
      "https://www.lesclesdudigital.fr/les-places-de-marche-btob-enrichissent-leurs-services/",
  },
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/76639d18e47d00f52a5d7bbf636a13083a07cba0845192f68c781b8c8e084ace.png",
    mediaLogoHeight: 42,
    mediaArticleOverview:
      "«FAIRE, c’est aussi plus de 160 000 commandes entre...»",
    mediaArticleOverviewMobile:
      "«FAIRE, c’est aussi plus de 160 000 commandes entre entreprises américaines...»",
    mediaArticleUrl:
      "https://www.neomag.fr/article/9702/marketplaces-b2b-un-modele-economique-pense-pour-les-commerces-independants",
  },
];

const NEWS_ARTICAL_INFO_DEU: IEuropeNewsArticle[] = [
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/8cba88477ee3c6ac5033a546d5efc5f4dde414d664d0683d7bd76572a715ac40.png",
    mediaLogoHeight: 28,
    mediaArticleOverview:
      "“Der Marktplatz Faire wurde 2017 gegründet und verzeichnet...”",
    mediaArticleOverviewMobile:
      "“Der Marktplatz Faire wurde 2017 gegründet und verzeichnet seitdem satte Zuwachsraten.”",
    mediaArticleUrl:
      "https://www.internetworld.de/b2b/online-marktplatz/marktplatz-faire-kleine-haendler-alternative-zu-amazon-2731389.html",
  },
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/f7bc09168e73af9759336020aaca78be269c3c8decc4a1d85c42bb2c95eb1eb4.png",
    mediaLogoHeight: 36,
    mediaArticleOverview:
      "“Von diesem kuratierten Portfolio aus unabhängigen Herstellern...”",
    mediaArticleOverviewMobile:
      "“Von diesem kuratierten Portfolio aus unabhängigen Herstellern profitieren...”",
    mediaArticleUrl:
      "https://babyundjunior.de/de/Handel/Grosse-Chancen-fuer-kleine-Haendler",
  },
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/5078fb52779a3d3daa34a3cc2b2524ce96240588d6651b9d2e88b54a7d3e39f1.png",
    mediaLogoHeight: 36,
    mediaArticleOverview:
      "“Für die Marken selbst bietet die Plattform mit „FaireDirect”...” ",
    mediaArticleOverviewMobile:
      "“Für die Marken selbst bietet die Plattform mit „FaireDirect” Vertriebs-, Marketing- und...” ",
    mediaArticleUrl:
      "https://lnk.to/Startup-Insider?utm_source=Startup+Insider+Daily&utm_campaign=b193eb9752-EMAIL_CAMPAIGN_2020_01_10_11_26_COPY_01&utm_medium=email&utm_term=0_6a5d9fa724-b193eb9752-138739046&mc_cid=b193eb9752&mc_eid=dafba9dbb5",
  },
];

const NEWS_ARTICAL_INFO_GBR: IEuropeNewsArticle[] = [
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/f89604188bb49418d7d4a90d782f33a6cd31319e63f1d3c11040d77eddcd0a07.png",
    mediaLogoHeight: 34,
    mediaArticleOverview:
      "“The battle to stock Europe’s independent retailers has...”",
    mediaArticleOverviewMobile:
      "“The battle to stock Europe’s independent retailers has intensified as US wholesale...”",
    mediaArticleUrl:
      "https://cdn.faire.com/static/asset/Faire_+Decacorn+Marketplace+Startup+Excels+in+European+Expansion.pdf",
  },
  {
    mediaLogoImageUrl:
      "https://cdn.faire.com/fastly/129c3c89ecb10e7a847ddd14978e12575f058be94941f66defaa777397d92c0f.png",
    mediaLogoHeight: 40,
    mediaArticleOverview:
      "“Since expanding the platform in March 2021, Faire...”",
    mediaArticleOverviewMobile:
      "“Since expanding the platform in March 2021, Faire has provided vital support...”",
    mediaArticleUrl:
      "https://tamebay.com/2022/03/faire-enable-sale-of-over-8-million-products-in-uk-in-first-year",
  },
];

export const EUROPE_NEWS_ARTICLES_GROUP: Partial<
  Record<keyof typeof THREE_LETTER_PRIMARY_COUNTRY_CODES, IEuropeNewsArticle[]>
> = {
  FRA: NEWS_ARTICAL_INFO_FRA,
  DEU: NEWS_ARTICAL_INFO_DEU,
  GBR: NEWS_ARTICAL_INFO_GBR,
};
